import { useState, useCallback } from 'react';
import { t } from '@lingui/macro';
import styled from '@emotion/styled';
import {
  Autocomplete,
  createFilterOptions,
  useTheme,
  type FilterOptionsState,
} from '@mui/material';
import { TextInput, TextInputProps } from './TextInput';
import type { ComboBoxProps, ComboBoxOption } from './types';
import { useGet } from 'lib/api';
import { debounce } from 'lodash';
import { Tooltip } from 'components/tooltip';
import { Avatar } from 'components/avatar';

export const parseSearch = (atSearch: boolean, search: string) => {
  if (atSearch) {
    if (search.charAt(0) === '@') {
      return search.substring(1).trim();
    }
    return '';
  }
  // Only allow safe characters for URL path parameters:
  // alphanumeric, spaces, dashes, underscores, and commas
  // This prevents issues with special characters that break URL routing
  return search.trim().replace(/[^a-zA-Z0-9\s\-_,]/g, '');
};

export const ComboBox = ({
  sx,
  options = [],
  label,
  placeholder,
  clearOnBlur = true,
  openText = t`Open`,
  loading = false,
  fullWidth = false,
  loadingText = t`Loading...`,
  noOptionsText = t`No options`,
  inputVariant = 'unstyled',
  freeSolo = true,
  InputProps,
  error,
  onBlur,
  onChange,
  onClose,
  onInputChange,
  open,
  api = null,
  optionsLimit,
  onKeyDown,
  onKey,
  groupBy = false,
  atSearch = false,
  hasAvatar = false,
  forcePopupIcon,
  popupIcon,
  apiVarsContext,
  allowEmptySearchFetch = false,
  disableOnEmptyApiOptions = true,
  emptyOptionsPlaceholder,
  ...rest
}: ComboBoxProps) => {
  const [search, setSearch] = useState('');
  const parsedSearch = parseSearch(atSearch, search);

  // Resolve vars if it's a function (table passes resolved array; form can pass apiVarsContext)
  const resolvedVars = api?.vars
    ? typeof api.vars === 'function'
      ? api.vars(apiVarsContext)
      : api.vars
    : [];

  const { data } = useGet(
    api?.url || null,
    // Search APIs must key on resolvedVars (e.g. org id) — otherwise React Query
    // returns stale options when context changes but the input search string does not.
    api?.search ? [api?.key, search, ...resolvedVars] : api?.key ? [api.key, ...resolvedVars] : '',
    api?.search ? [parsedSearch, ...resolvedVars] : resolvedVars,
    undefined,
    api?.revalidate !== false,
    {
      enabled: (() => {
        if (!api) return false;
        if (api.search) {
          if (allowEmptySearchFetch) {
            return typeof api.vars !== 'function' || resolvedVars.length > 0;
          }
          return Boolean(parsedSearch) && parsedSearch.length >= 1;
        }
        return typeof api.vars !== 'function' || resolvedVars.length > 0;
      })(),
      ...(api?.options ?? {}),
    }
  );
  const selectOptions = data && api ? api.responseTransform(data) : options;

  const theme = useTheme();
  const isApiEmpty =
    disableOnEmptyApiOptions &&
    !!api &&
    data !== undefined &&
    Array.isArray(selectOptions) &&
    selectOptions.length === 0 &&
    !!emptyOptionsPlaceholder;
  const effectiveDisabled = (rest as { disabled?: boolean }).disabled || isApiEmpty;
  const effectivePlaceholder = placeholder;
  const palette = theme.palette as {
    bg?: { disabledSubtle?: string };
    text?: { disabled?: string };
  };
  const disabledInputSx = effectiveDisabled
    ? {
        '& .MuiInputBase-root': {
          backgroundColor: palette.bg?.disabledSubtle ?? theme.palette.action.disabledBackground,
          opacity: 0.92,
          cursor: 'not-allowed',
          '& input, & .MuiAutocomplete-input': {
            cursor: 'not-allowed',
            color: palette.text?.disabled ?? theme.palette.action.disabled,
          },
        },
      }
    : {};
  const inputChangeApi = useCallback(
    debounce((newInputValue: string) => {
      setSearch(newInputValue);
    }, 250),
    []
  );

  // limit amount of options shown
  const defaultFilterOptions = createFilterOptions<ComboBoxOption>();
  // Check if custom filterOptions is provided via rest props
  const customFilterOptions = (
    rest as {
      filterOptions?: (
        opts: ComboBoxOption[],
        state?: FilterOptionsState<ComboBoxOption>
      ) => ComboBoxOption[];
    }
  ).filterOptions;

  // Build filterOptions function
  // Original behavior: when optionsLimit is NOT set, use passthrough function
  // When optionsLimit IS set, apply defaultFilterOptions (text search) and slice
  const filterOptions = customFilterOptions
    ? (opts: ComboBoxOption[], state: FilterOptionsState<ComboBoxOption>) => {
        // Apply custom filter first (e.g., remove "partially-approved")
        let filtered = customFilterOptions(opts, state);

        // If optionsLimit is set OR atSearch is enabled, apply default MUI text-based filtering
        if (optionsLimit || atSearch) {
          if (atSearch) {
            const searchValue = parseSearch(atSearch, state.inputValue);
            if (!searchValue) return [];
            filtered = defaultFilterOptions(filtered, { ...state, inputValue: searchValue });
          } else {
            // Apply defaultFilterOptions for text matching (MUI's standard behavior)
            filtered = defaultFilterOptions(filtered, state);
          }

          // Apply optionsLimit if set
          if (optionsLimit) {
            return filtered.slice(0, optionsLimit);
          }
          return filtered;
        }

        // When optionsLimit is NOT set and atSearch is false: return custom filtered options only
        // (no text search filtering, no limit - matches passthrough behavior)
        return filtered;
      }
    : (opts: ComboBoxOption[], state: FilterOptionsState<ComboBoxOption>) => {
        // Original behavior: when optionsLimit is NOT set, use passthrough function
        // When optionsLimit IS set, apply defaultFilterOptions (text search) and slice
        if (atSearch) {
          const searchValue = parseSearch(atSearch, state.inputValue);
          if (!searchValue) return [];
          // When atSearch is enabled, always apply filtering
          const filtered = defaultFilterOptions(opts, { ...state, inputValue: searchValue });
          // Apply optionsLimit if set
          return optionsLimit ? filtered.slice(0, optionsLimit) : filtered;
        }
        // When freeSolo is false, always apply filtering
        if (!freeSolo) {
          const filtered = defaultFilterOptions(opts, state);
          return optionsLimit ? filtered.slice(0, optionsLimit) : filtered;
        }
        // When optionsLimit is NOT set, return options as-is (passthrough)
        if (!optionsLimit) {
          return opts;
        }
        // When optionsLimit IS set, apply default filtering and slice
        return defaultFilterOptions(opts, state).slice(0, optionsLimit);
      };

  const autocomplete = (
    <Autocomplete
      {...rest}
      disabled={effectiveDisabled}
      selectOnFocus
      clearOnBlur={clearOnBlur}
      freeSolo={freeSolo}
      fullWidth={fullWidth}
      openText={openText}
      open={open}
      loading={loading}
      loadingText={loadingText}
      forcePopupIcon={forcePopupIcon}
      popupIcon={popupIcon}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 215,
            marginTop: '4px',
            '& .MuiAutocomplete-listbox': {
              padding: 0,
            },
          },
        },
      }}
      {...(typeof options?.[0] === 'object'
        ? {
            isOptionEqualToValue: (option: ComboBoxOption, value: ComboBoxOption): boolean => {
              if (typeof value === 'object' && typeof option === 'object') {
                return (
                  value.id === option.id ||
                  (value.value != null && value.value === option.value) ||
                  value.id === option.value ||
                  value.value === option.id
                );
              }

              if (typeof value === 'object') {
                return (
                  value.id === (option as unknown as string) ||
                  value.value === (option as unknown as string)
                );
              }

              if (typeof option === 'object') {
                return option.id === value || option.value === value;
              }

              return value === option;
            },
          }
        : {})}
      noOptionsText={noOptionsText}
      options={selectOptions}
      groupBy={groupBy ? option => (option as ComboBoxOption).groupByHeader || '' : undefined}
      renderGroup={params =>
        groupBy ? (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        ) : (
          ''
        )
      }
      sx={{
        m: 1,
        width: '100%',
        '.MuiInputBase-root': {
          padding: 0,
        },
        '.MuiFormControl-root': {
          '.MuiInputBase-root': {
            padding: 0,
          },
        },
        '.MuiAutocomplete-endAdornment': {
          gap: '6px',
        },
        ...disabledInputSx,
        ...sx,
      }}
      onClose={onClose}
      getOptionDisabled={({ disabled = false }: ComboBoxOption) => disabled}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;
        if (option?.handleClick) return '';
        return option?.label;
      }}
      filterOptions={
        customFilterOptions
          ? filterOptions
          : optionsLimit || !freeSolo || atSearch
            ? filterOptions
            : optionsValues => optionsValues as ComboBoxOption[]
      }
      onInputChange={(event: React.SyntheticEvent, newInputValue: string, reason: string) => {
        if (onInputChange) onInputChange(event, newInputValue, reason);
        if (api?.search) inputChangeApi(newInputValue);
      }}
      onChange={(event, values, reason) => {
        onChange?.(event, values, reason);
      }}
      onBlur={event => {
        onBlur?.();
      }}
      renderOption={(prop, option) => {
        const isClearOption = option.id === '__clear__' && option.CustomElement;
        return (
          <Tooltip title={option.tooltip || ''}>
            <Li
              {...prop}
              key={option.id}
              className={`${prop.className || ''} ${isClearOption ? 'clear-field-option-li' : ''}`}
            >
              {option.CustomElement ? (
                <option.CustomElement
                  label={option.label}
                  handleClick={option.handleClick}
                  {...option.customProps}
                />
              ) : (
                <>
                  {hasAvatar ? (
                    <>
                      <Avatar
                        size="sm"
                        letters={option.label}
                        color={option.avatarColor ? `#${option.avatarColor}` : ''}
                      />
                      <Spacing />
                    </>
                  ) : null}
                  <span onClick={option.handleClick}>{option.label}</span>
                </>
              )}
            </Li>
          </Tooltip>
        );
      }}
      renderInput={params => {
        const selected = options.find(o => o.label === rest?.value);
        if (selected?.CustomElement) {
          return (
            <>
              <HiddenInput>
                <TextInput
                  {...(rest as TextInputProps)}
                  {...(params as TextInputProps)}
                  variant={inputVariant === 'unstyled' ? 'standard' : inputVariant}
                  InputProps={{ ...params.InputProps, ...InputProps }}
                />
              </HiddenInput>
              <selected.CustomElement label={selected.label} {...selected.customProps} />
            </>
          );
        }
        const textParams = {
          ...params,
          InputLabelProps: { ...params.InputLabelProps, shrink: true, required: rest.required },
        };
        return (
          <TextInput
            autoFocus={Boolean(open)}
            {...textParams}
            error={error}
            color={error ? 'error' : 'primary'}
            placeholder={loading ? loadingText : effectivePlaceholder}
            label={label}
            helperText={error ? label : ''}
            {...(rest as TextInputProps)}
            disabled={effectiveDisabled}
            variant={inputVariant === 'unstyled' ? 'standard' : inputVariant}
            InputProps={{ ...params.InputProps, ...InputProps }}
            onKeyDown={e => {
              // for some reason onKeyDown don't work in certain scenarios
              if (onKey) onKey(e);
              if (onKeyDown) onKeyDown(e);
            }}
          />
        );
      }}
    />
  );

  if (isApiEmpty && emptyOptionsPlaceholder) {
    return (
      <Tooltip title={emptyOptionsPlaceholder} placement="top">
        <div>{autocomplete}</div>
      </Tooltip>
    );
  }

  return autocomplete;
};

const Li = styled.li`
  font-size: 0.875rem;
  margin: 0;
  padding: 10px 16px !important;
  border-radius: 0;
  min-height: 40px;

  &.clear-field-option-li {
    position: sticky;
    bottom: 0;
    padding: 0 !important;
    min-height: unset;
    background: ${p => p.theme.palette.bg.primary};
    z-index: 1;
  }
`;

export const GroupHeader = styled('div')(({ theme }) => ({
  ...theme.typography.bodyXs,
  fontWeight: 600,
  position: 'sticky',
  top: '-8px',
  padding: '8px 10px',
  color: theme.palette.text.placeholder,
  backgroundColor: theme.palette.bg.primary,
  zIndex: 99,
}));

export const GroupItems = styled('ul')({
  padding: 0,
});

export const HiddenInput = styled.span`
  visibility: hidden;
  width: 0px;
  display: block;
  height: 0px;
`;

const Spacing = styled.span`
  min-width: 15px;
  height: 15px;
`;
